import {
  Checkbox,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useWindowHeight } from '@react-hook/window-size'
import get from 'lodash/get'
import xor from 'lodash/xor'
import { Dispatch, Fragment, SetStateAction, useEffect } from 'react'
import { SerialNumber } from 'utoniq-ui'

import {
  ConsoleDeliveryItemBoxFragment,
  DeliveryStatus,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { DeliveryManagementTag } from '@/components/organisms'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { Form, useForm, UseFormProps, useMessage, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

type DeliveryManagementFormProps = {
  loading: boolean
  deliveries: Maybe<{ edges: { node: ConsoleDeliveryItemBoxFragment }[] }>
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
  setDisableDeliveried: Dispatch<SetStateAction<boolean>>
  onSubmit: (value: string[]) => Promise<void>
}

type DeliveryManagementValues = {
  selectedId: string[]
}

const schema: UseFormProps['schema'] = o => ({
  selectedId: o.array().items(o.string()).optional(),
})

export const DeliveryManagementForm: Organism<DeliveryManagementFormProps> = ({
  loading,
  hasNextPage,
  deliveries,
  onFetchMore,
  onSubmit,
  setDisableDeliveried,
}) => {
  const { t } = useTranslation('consoleDeliveryManagement')
  const { showError } = useMessage()
  const { setValue, watch, handleSubmit } = useForm<DeliveryManagementValues>({
    schema,
    defaultValues,
  })
  const windowHeight = useWindowHeight()
  const selectedId = watch('selectedId', [])

  useEffect(() => {
    setDisableDeliveried(!selectedId.length)
  }, [selectedId, setDisableDeliveried])

  const handleOnSubmit = handleSubmit(async values => {
    await onSubmit(values.selectedId)
      .then(() => {
        setValue('selectedId', [])
      })
      .catch(showError)
  })

  const headers = [
    { key: 'itemSerial.serialNumber', width: 75 },
    { key: 'status', width: 72 },
    { key: 'item.title', width: 142 },
    { key: 'itemSerial.sku.unitName', width: 80 },
    { key: 'user.email', width: 140 },
    { key: 'deliveryInfo.fullName', width: 90 },
    { key: 'deliveryInfo.phoneNumber', width: 108 },
    { key: 'deliveryInfo.address.postalCode', width: 76 },
    { key: 'deliveryInfo.address.fullAddress', width: 140 },
    { key: 'formattedAppliedAt', width: 95 },
    { key: 'formattedShippedAt', width: 95 },
  ]

  return (
    <OrganismFrame>
      <Form id="delivery-management-form" onSubmit={handleOnSubmit}>
        <TableContainer sx={{ maxHeight: windowHeight - 192 }}>
          <Table defaultChecked>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ borderBottom: 'none' }}>
                  <Checkbox
                    color="primary"
                    indeterminate={
                      !!deliveries?.edges.length &&
                      selectedId.length > 0 &&
                      selectedId.length < deliveries?.edges.length
                    }
                    checked={
                      selectedId.length > 0 &&
                      selectedId.length === deliveries?.edges.length
                    }
                    onChange={() => {
                      setValue(
                        'selectedId',
                        selectedId.length === deliveries?.edges.length
                          ? []
                          : deliveries?.edges.map(({ node }) => node.id) || []
                      )
                    }}
                  />
                </TableCell>
                {headers.map(({ key, width }) => (
                  <TableCell
                    sx={{
                      border: 'none',
                      height: 40,
                      p: 0,
                      minWidth: width,
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="text.hint"
                      sx={{ width: width }}
                    >
                      {t(key)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableScrollFetcher
              nodes={deliveries?.edges.map(({ node }) => (
                <Fragment key={node.id}>
                  <TableCell padding="checkbox" sx={{ borderBottom: 'none' }}>
                    <Checkbox
                      color="primary"
                      checked={
                        selectedId.findIndex(id => id === node.id) >= 0 &&
                        node.status !== DeliveryStatus.Shipped
                      }
                      disabled={node.status === DeliveryStatus.Shipped}
                      onClick={() =>
                        setValue('selectedId', xor(selectedId, [node.id]))
                      }
                    />
                  </TableCell>
                  {headers.map(({ key, width }, index) => (
                    <TableCell
                      key={index}
                      sx={{ px: 0, width, py: 3, borderBottom: 'none' }}
                    >
                      <Stack
                        sx={{
                          width,
                          px: 2,
                          borderRightWidth:
                            index + 1 === headers.length ? 0 : 1,
                          borderRightStyle: 'solid',
                          borderRightColor: 'grey.100',
                        }}
                      >
                        {key === 'itemSerial.serialNumber' && (
                          <SerialNumber
                            label={node.itemSerial?.formattedSerialNumber}
                          />
                        )}
                        {key === 'status' && (
                          <DeliveryManagementTag status={node.status} />
                        )}
                        {key !== 'status' && key !== 'itemSerial.serialNumber' && (
                          <Typography
                            variant="overline"
                            color="text.hint"
                            sx={{ ...styleSets.lineCamp(1) }}
                          >
                            {get(node, key) || '-'}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  ))}
                </Fragment>
              ))}
              component={TableRow}
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              colSpan={headers.length}
              emptyMessage={t('deliveryManagementEmpty')}
            />
          </Table>
        </TableContainer>
      </Form>
    </OrganismFrame>
  )
}

const defaultValues: DeliveryManagementValues = {
  selectedId: [],
}
